import React, { useCallback, useEffect, useState } from "react";
import {
  Typography,
  Divider,
  Row,
  Col,
  Card,
  Collapse,
  Switch,
  message,
  Spin,
  Tag,
  Button,
  List,
  Dropdown,
} from "antd";
import { ReloadOutlined, DownOutlined, LinkOutlined, DeleteOutlined } from "@ant-design/icons";
import { useAuth0 } from "../../react-auth0-spa";
import config from "../../api_config.json";
import { useAppContext } from "../../context";
import { getServiceClusterEntries } from "./serviceClusters";

const { Title, Text } = Typography;

const mapOfServices = {
  "ecs-parse-dashboard-dev":
    "https://parse-dashboard.dev-onoranzefunebricloud.com/",
  "ecs-parse-dashboard-prod":
    "https://parse-dashboard.onoranzefunebricloud.com/",
  "ecs-grafana-prod": "https://grafana.onoranzefunebricloud.com/",
  "ecs-grafana-dev": "https://grafana.dev-onoranzefunebricloud.com/",
  "eterno-dev": "https://eterno.dev-onoranzefunebricloud.com/",
  "eterno-prod": "https://eterno.onoranzefunebricloud.com/",
  "label-studio-prod": "https://label-studio.onoranzefunebricloud.com/",
  "bugsink-prod": "https://bugsink.onoranzefunebricloud.com/",
};

const getServiceLinks = (clusterName, serviceName) => {
  const region = "eu-central-1";
  const awsConsoleBase = `https://${region}.console.aws.amazon.com`;
  const isEterno = serviceName.startsWith("eterno");
  const cleanedServiceName = isEterno
    ? serviceName
    : serviceName.replace(/-dev|-prod/g, "");
  const suffix = serviceName.includes("-prod") ? "$252Fprod" : "$252Fdev";

  let url = mapOfServices[serviceName];

  // Handle preview environment URLs for eterno services
  if (isEterno && !["eterno-dev", "eterno-prod"].includes(serviceName)) {
    url = `https://${serviceName}.onoranzefunebricloud.com`;
  }

  return {
    ecs: `${awsConsoleBase}/ecs/v2/clusters/${clusterName}/services/${serviceName}/health?region=${region}`,
    cloudwatch: `${awsConsoleBase}/cloudwatch/home?region=${region}#logsV2:log-groups/log-group/$252Faws$252Fecs$252F${clusterName}$252F${cleanedServiceName}${suffix}`,
    url,
  };
};

// Extract base name from service name
// "eterno-feat-pet" -> "feat-pet"
// "eterno-next-feat-pet" -> "feat-pet"
const extractBaseName = (serviceName) => {
  if (serviceName.startsWith("eterno-next-")) {
    return serviceName.replace("eterno-next-", "");
  }
  return serviceName.replace("eterno-", "");
};

// Create pairs: { baseName, eterno, eternoNext }
const pairPreviewEnvironments = (previews) => {
  const pairs = {};
  previews.forEach((service) => {
    const baseName = extractBaseName(service.serviceName);
    const isNext = service.serviceName.startsWith("eterno-next-");
    if (!pairs[baseName]) {
      pairs[baseName] = { baseName, eterno: null, eternoNext: null };
    }
    pairs[baseName][isNext ? "eternoNext" : "eterno"] = service;
  });
  return Object.values(pairs).sort((a, b) =>
    a.baseName.localeCompare(b.baseName)
  );
};

const groupPreviewEnvironments = (clusters) => {
  const groupedClusters = {};

  getServiceClusterEntries(clusters).forEach(([clusterName, services]) => {
    // Initialize arrays for regular services and preview environments
    const regularServices = [];
    const previewEnvironments = [];

    services.forEach((service) => {
      // Add clusterName to each service object
      const serviceWithCluster = { ...service, clusterName };

      if (
        service.serviceName.startsWith("eterno-") &&
        ![
          "eterno-dev",
          "eterno-next-dev",
          "eterno-prod",
          "eterno-next-prod",
        ].includes(service.serviceName)
      ) {
        previewEnvironments.push(serviceWithCluster);
      } else {
        regularServices.push(serviceWithCluster);
      }
    });

    // Add regular services to the cluster
    if (regularServices.length > 0) {
      groupedClusters[clusterName] = regularServices;
    }

    // Add preview environments as paired list
    if (previewEnvironments.length > 0) {
      const pairedEnvironments = pairPreviewEnvironments(previewEnvironments);
      groupedClusters["Eterno - Preview Environments"] = {
        isPairedPreviewEnv: true,
        pairs: pairedEnvironments,
      };
    }
  });

  return groupedClusters;
};

const Servizi = () => {
  const [loading, setLoading] = useState(false);
  const [clusters, setClusters] = useState({});
  const [updatingServices, setUpdatingServices] = useState(new Set());
  const { isProd } = useAppContext();
  const { getTokenSilently } = useAuth0();
  const [countdown, setCountdown] = useState(29);

  const fetchData = useCallback(
    async (from) => {
      try {
        const token = await getTokenSilently();

        const response = await fetch(
          `${config.baseUrl[isProd ? "prod" : "dev"]}/services`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const responseData = await response.json();
        return responseData;
      } catch (error) {
        console.error(error);
      }
    },
    [isProd, getTokenSilently]
  );

  const toggleService = async (serviceName, desiredCount, clusterName) => {
    try {
      // Immediately add service to updating set
      setUpdatingServices((prev) => new Set(prev).add(serviceName));

      const token = await getTokenSilently();
      const newDesiredCount = desiredCount === 0 ? 1 : 0;

      await fetch(
        `${config.baseUrl[isProd ? "prod" : "dev"]
        }/services/${clusterName}/${serviceName}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ desiredCount: newDesiredCount }),
        }
      );

      // Refresh data after toggle
      const { data } = await fetchData();
      const groupedData = groupPreviewEnvironments(data);
      setClusters(groupedData);
      message.success(
        `Service ${serviceName} ${newDesiredCount === 0 ? "stopped" : "started"
        }`
      );
    } catch (error) {
      console.error(error);
      message.error("Failed to toggle service");
    } finally {
      // Remove service from updating set
      setUpdatingServices((prev) => {
        const newSet = new Set(prev);
        newSet.delete(serviceName);
        return newSet;
      });
    }
  };

  // Toggle both services in a pair simultaneously
  const togglePairedServices = async (pair) => {
    const servicesToToggle = [pair.eterno, pair.eternoNext].filter(Boolean);

    try {
      // Add all services to updating set
      servicesToToggle.forEach((service) => {
        setUpdatingServices((prev) => new Set(prev).add(service.serviceName));
      });

      const token = await getTokenSilently();

      // If any is running, stop both; if both stopped, start both
      const anyRunning = servicesToToggle.some((s) => s.desiredCount > 0);
      const newDesiredCount = anyRunning ? 0 : 1;

      // Toggle all services in parallel
      await Promise.all(
        servicesToToggle.map((service) =>
          fetch(
            `${config.baseUrl[isProd ? "prod" : "dev"]}/services/${service.clusterName}/${service.serviceName}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ desiredCount: newDesiredCount }),
            }
          )
        )
      );

      // Refresh data
      const { data } = await fetchData();
      const groupedData = groupPreviewEnvironments(data);
      setClusters(groupedData);
      message.success(
        `Preview "${pair.baseName}" ${newDesiredCount === 0 ? "stopped" : "started"}`
      );
    } catch (error) {
      console.error(error);
      message.error("Failed to toggle preview environment");
    } finally {
      // Remove all services from updating set
      servicesToToggle.forEach((service) => {
        setUpdatingServices((prev) => {
          const newSet = new Set(prev);
          newSet.delete(service.serviceName);
          return newSet;
        });
      });
    }
  };

  // Get combined status of a pair
  const getPairStatus = (pair) => {
    const eternoRunning = pair.eterno?.desiredCount > 0;
    const eternoNextRunning = pair.eternoNext?.desiredCount > 0;

    if (eternoRunning && eternoNextRunning) return "running";
    if (!eternoRunning && !eternoNextRunning) return "stopped";
    return "mixed";
  };

  // Check if any service in pair is updating
  const isPairUpdating = (pair) => {
    const eternoUpdating =
      pair.eterno &&
      (updatingServices.has(pair.eterno.serviceName) ||
        pair.eterno.runningCount !== pair.eterno.desiredCount);
    const eternoNextUpdating =
      pair.eternoNext &&
      (updatingServices.has(pair.eternoNext.serviceName) ||
        pair.eternoNext.runningCount !== pair.eternoNext.desiredCount);
    return eternoUpdating || eternoNextUpdating;
  };

  // Check if pair is toggleable
  const isPairToggleable = (pair) => {
    const eternoToggleable =
      !pair.eterno || isServiceToggleable(pair.eterno.serviceName);
    const eternoNextToggleable =
      !pair.eternoNext || isServiceToggleable(pair.eternoNext.serviceName);
    return eternoToggleable && eternoNextToggleable;
  };

  const isServiceToggleable = (serviceName) => {
    if (!isProd) return true;
    const allowedServices = ["ecs-parse-dashboard-prod", "ecs-grafana-prod", "label-studio-prod"];

    return (
      (serviceName.startsWith("eterno-") &&
        !["eterno-prod", "eterno-next-prod"].includes(serviceName)) ||
      allowedServices.includes(serviceName)
    );
  };

  const handleManualRefresh = async () => {
    setLoading(true);
    const { data } = await fetchData();
    const groupedData = groupPreviewEnvironments(data);
    setClusters(groupedData);
    setLoading(false);
    setCountdown(29); // Reset countdown after manual refresh
  };

  useEffect(() => {
    const fetchAndSetData = async () => {
      setLoading(true);
      const { data } = await fetchData();
      const groupedData = groupPreviewEnvironments(data);
      setClusters(groupedData);
      setLoading(false);
      setCountdown(29); // Reset countdown after fetch
    };

    // Initial fetch
    fetchAndSetData();

    // Set up interval for auto-refresh
    const refreshInterval = setInterval(fetchAndSetData, 30000);

    // Set up countdown interval
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 29));
    }, 1000);

    // Cleanup intervals on component unmount
    return () => {
      clearInterval(refreshInterval);
      clearInterval(countdownInterval);
    };
  }, [fetchData]);

  return (
    <div style={{ margin: "16px 16px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Servizi
        </Title>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Tag color="blue">Auto-Refresh in {countdown}s</Tag>
          <Button
            type="primary"
            size="small"
            icon={<ReloadOutlined spin={loading} />}
            onClick={loading ? undefined : handleManualRefresh}
            style={{
              opacity: loading ? 0.5 : 1,
            }}
            disabled={loading}
          >
            Refresh now
          </Button>
        </div>
      </div>
      <Divider />
      <Spin spinning={loading} size="large">
        <div style={{ minHeight: "200px" }}>
          {!loading &&
            Object.entries(clusters).map(([clusterName, clusterData]) => (
              <div key={clusterName} style={{ marginBottom: "32px" }}>
                <Collapse defaultActiveKey={[clusterName]}>
                  <Collapse.Panel
                    header={`Cluster: ${clusterName}`}
                    key={clusterName}
                  >
                    {/* Preview Environments: render as list */}
                    {clusterData.isPairedPreviewEnv ? (
                      <List
                        itemLayout="horizontal"
                        dataSource={clusterData.pairs}
                        renderItem={(pair) => {
                          const status = getPairStatus(pair);
                          const isUpdating = isPairUpdating(pair);
                          const toggleable = isPairToggleable(pair);

                          const statusColors = {
                            running: { bg: "#e8f5e9", border: "#c8e6c9", tag: "green" },
                            stopped: { bg: "#ffebee", border: "#ffcdd2", tag: "red" },
                            mixed: { bg: "#fff3e0", border: "#ffe0b2", tag: "orange" },
                          };
                          const colors = statusColors[status];

                          const eternoLinks = pair.eterno
                            ? getServiceLinks(pair.eterno.clusterName, pair.eterno.serviceName)
                            : null;
                          const eternoNextLinks = pair.eternoNext
                            ? getServiceLinks(pair.eternoNext.clusterName, pair.eternoNext.serviceName)
                            : null;

                          // Build dropdown menu items
                          const menuItems = [];
                          if (eternoLinks) {
                            menuItems.push(
                              { key: "ecs", label: <a href={eternoLinks.ecs} target="_blank" rel="noreferrer">ECS Console</a> },
                              { key: "logs", label: <a href={eternoLinks.cloudwatch} target="_blank" rel="noreferrer">CloudWatch Logs</a> }
                            );
                          }
                          if (eternoNextLinks) {
                            menuItems.push(
                              { type: "divider" },
                              { key: "next-ecs", label: <a href={eternoNextLinks.ecs} target="_blank" rel="noreferrer">Next - ECS Console</a> },
                              { key: "next-logs", label: <a href={eternoNextLinks.cloudwatch} target="_blank" rel="noreferrer">Next - CloudWatch Logs</a> }
                            );
                          }
                          // Add delete action
                          const destroyWorkflowUrl = `https://github.com/onoranzefunebricloud/eterno/actions/workflows/manual-destroy-preview-environment.yml`;
                          menuItems.push(
                            { type: "divider" },
                            {
                              key: "delete",
                              danger: true,
                              icon: <DeleteOutlined />,
                              label: "Elimina preview",
                              onClick: () => {
                                navigator.clipboard.writeText(pair.baseName);
                                message.success(`"${pair.baseName}" copiato negli appunti`);
                                window.open(destroyWorkflowUrl, "_blank");
                              },
                            }
                          );

                          return (
                            <List.Item
                              key={pair.baseName}
                              style={{
                                backgroundColor: isUpdating ? "#fff3e0" : colors.bg,
                                border: `1px solid ${isUpdating ? "#ffe0b2" : colors.border}`,
                                borderRadius: "8px",
                                marginBottom: "8px",
                                padding: "12px 16px",
                                opacity: status === "stopped" ? 0.6 : 1,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "16px",
                                  width: "100%",
                                }}
                              >
                                {/* Name and status */}
                                <div style={{ minWidth: "200px", flex: 1 }}>
                                  <Text strong style={{ fontSize: "14px" }}>
                                    {pair.baseName}
                                  </Text>
                                  <div style={{ marginTop: "4px" }}>
                                    <Tag color={isUpdating ? "orange" : colors.tag}>
                                      {isUpdating
                                        ? "Updating..."
                                        : status === "running"
                                        ? "Running"
                                        : status === "stopped"
                                        ? "Stopped"
                                        : "Mixed"}
                                    </Tag>
                                    {status === "mixed" && !isUpdating && (
                                      <Text
                                        type="secondary"
                                        style={{ fontSize: "12px", marginLeft: "8px" }}
                                      >
                                        (eterno-php: {pair.eterno?.desiredCount > 0 ? "on" : "off"},
                                        eterno-next: {pair.eternoNext?.desiredCount > 0 ? "on" : "off"})
                                      </Text>
                                    )}
                                  </div>
                                </div>

                                {/* Actions: Open button + Dropdown */}
                                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                                  {eternoLinks?.url && (
                                    <Button
                                      type="primary"
                                      size="small"
                                      icon={<LinkOutlined />}
                                      href={eternoLinks.url}
                                      target="_blank"
                                    >
                                      Apri
                                    </Button>
                                  )}
                                  <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
                                    <Button size="small">
                                      Actions <DownOutlined />
                                    </Button>
                                  </Dropdown>
                                </div>

                                {/* Toggle - far right */}
                                {toggleable && (
                                  <Switch
                                    checked={status === "running" || status === "mixed"}
                                    onChange={() => togglePairedServices(pair)}
                                    disabled={isUpdating}
                                  />
                                )}
                              </div>
                            </List.Item>
                          );
                        }}
                      />
                    ) : (
                      /* Regular services: render as cards */
                      <Row gutter={[16, 16]}>
                        {clusterData.map((service) => {
                          const isToggleable = isServiceToggleable(
                            service.serviceName
                          );
                          const isStable =
                            service.runningCount === service.desiredCount;
                          const isUpdating =
                            !isStable || updatingServices.has(service.serviceName);

                          return (
                            <Col
                              key={service.serviceName}
                              xs={24}
                              sm={12}
                              md={8}
                              lg={6}
                            >
                              <Spin spinning={isUpdating}>
                                <Card
                                  loading={loading}
                                  hoverable
                                  style={{
                                    borderRadius: "8px",
                                    opacity: service.runningCount === 0 ? 0.4 : 1,
                                    backgroundColor: isUpdating
                                      ? "#fff3e0"
                                      : service.runningCount === 0
                                        ? "#ffebee"
                                        : "#e8f5e9",
                                    border: isUpdating
                                      ? "1px solid #ffe0b2"
                                      : service.runningCount === 0
                                        ? "1px solid #ffcdd2"
                                        : "1px solid #c8e6c9",
                                    boxShadow: isUpdating
                                      ? "0 0 4px rgba(255,165,0,0.2)"
                                      : service.runningCount === 0
                                        ? "0 0 4px rgba(255,0,0,0.2)"
                                        : "0 0 4px rgba(0,255,0,0.1)",
                                  }}
                                  title={service.serviceName.replace("ecs-", "")}
                                  extra={
                                    isToggleable && (
                                      <Switch
                                        checked={service.desiredCount > 0}
                                        onChange={() =>
                                          toggleService(
                                            service.serviceName,
                                            service.desiredCount,
                                            service.clusterName
                                          )
                                        }
                                        disabled={isUpdating}
                                      />
                                    )
                                  }
                                >
                                  <div style={{ textAlign: "center" }}>
                                    <Text
                                      strong
                                      style={{
                                        fontSize: "16px",
                                        color: isUpdating ? "#ff9800" : "inherit",
                                      }}
                                    >
                                      {service.runningCount} / {service.desiredCount}
                                    </Text>
                                    <Text
                                      style={{ display: "block", marginTop: "8px" }}
                                    >
                                      {isUpdating ? "Updating..." : "Tasks Running"}
                                    </Text>
                                  </div>
                                  <Divider style={{ margin: "12px 0" }} />
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-around",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {(() => {
                                      const links = getServiceLinks(
                                        service.clusterName,
                                        service.serviceName
                                      );
                                      return (
                                        <>
                                          <a
                                            href={links.ecs}
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            ECS
                                          </a>
                                          <a
                                            href={links.cloudwatch}
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            CloudWatch
                                          </a>
                                          {links.url && (
                                            <a
                                              href={links.url}
                                              target="_blank"
                                              rel="noreferrer"
                                            >
                                              Public URL
                                            </a>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </Card>
                              </Spin>
                            </Col>
                          );
                        })}
                      </Row>
                    )}
                  </Collapse.Panel>
                </Collapse>
              </div>
            ))}
        </div>
      </Spin>
    </div>
  );
};

export default Servizi;
